/**
 * Centralized formatting utilities for the Loan Manager app.
 * All currency, number, date and percentage formatting lives here.
 */

// ── Currency ────────────────────────────────────────────────────

const audFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const audFormatterDecimals = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const audFormatterFlexibleDecimals = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/** Format a number as AUD currency with no decimals. e.g. $1,250,000 */
export function fmtCurrency(value: number | string | null | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  if (isNaN(num)) return '$0';
  return audFormatter.format(num);
}

/** Format a number as AUD currency with 2 decimal places. e.g. $1,250.75 */
export function fmtCurrencyExact(value: number | string | null | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  if (isNaN(num)) return '$0.00';
  return audFormatterDecimals.format(num);
}

/** Format a number as AUD currency with up to 2 decimal places. e.g. $1,250, $1,250.5, $1,250.75 */
export function fmtCurrencyTrimmed(value: number | string | null | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  if (isNaN(num)) return '$0';
  return audFormatterFlexibleDecimals.format(num);
}

/** Compact currency: $1.2M, $450K, $1,200 */
export function fmtCurrencyCompact(value: number | string | null | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  if (isNaN(num)) return '$0';
  if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
  if (Math.abs(num) >= 1_000) return `$${(num / 1_000).toFixed(0)}K`;
  return audFormatter.format(num);
}

// ── Numbers ─────────────────────────────────────────────────────

const numFormatter = new Intl.NumberFormat('en-AU');

/** Format a plain number with locale grouping. e.g. 1,234,567 */
export function fmtNumber(value: number | string | null | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  if (isNaN(num)) return '0';
  return numFormatter.format(num);
}

/** Format a plain number with locale grouping and configurable decimals. */
export function formatNumber(value: number, maximumFractionDigits = 3): string {
  return new Intl.NumberFormat('en-AU', {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(value);
}

// ── Percentages ─────────────────────────────────────────────────

/** Format a percentage. e.g. 12.5% */
export function fmtPercent(value: number | string | null | undefined, decimals = 1): string {
  const num = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  if (isNaN(num)) return '0%';
  return `${num.toFixed(decimals)}%`;
}

/** Format basis points. e.g. 250 bps */
export function fmtBps(value: number | string | null | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  if (isNaN(num)) return '0 bps';
  return `${(num * 10000).toFixed(0)} bps`;
}

/** Format an annual percent value as basis points. e.g. 2.5 -> 250 bps */
export function formatAnnualBps(value: number, maximumFractionDigits = 0): string {
  return `${formatNumber(value * 100, maximumFractionDigits)} bps`;
}

/** Format a decimal ratio as a percentage. e.g. 0.0512 -> 5.12% */
export function formatRatioPercent(value: number, maximumFractionDigits = 3): string {
  return `${formatNumber(value * 100, maximumFractionDigits)}%`;
}

/** Format an annual percent value directly. e.g. 5.12 -> 5.12% */
export function formatAnnualPercent(value: number, maximumFractionDigits = 3): string {
  return `${formatNumber(value, maximumFractionDigits)}%`;
}

/** Format a year duration. e.g. 1.25 years */
export function formatYears(value: number, maximumFractionDigits = 3): string {
  return `${formatNumber(value, maximumFractionDigits)} years`;
}

// ── Dates ───────────────────────────────────────────────────────

const dateFormatter = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const dateOnlyFormatter = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

const dateTimeFormatter = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const shortDateFormatter = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'short',
});

function parseDateOnlyValue(value: string | Date): Date | null {
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
  }

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));

  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }

  return parsed;
}

/** Format a date-only business date. e.g. 15 Mar 2026 */
export function fmtDateOnly(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = parseDateOnlyValue(value);
  if (!d) return '—';
  return dateOnlyFormatter.format(d);
}

/** Format a date. e.g. 15 Mar 2026 */
export function fmtDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return '—';
  return dateFormatter.format(d);
}

/** Format a date with time. e.g. 15 Mar 2026, 02:30 pm */
export function fmtDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return '—';
  return dateTimeFormatter.format(d);
}

/** Short date without year. e.g. 15 Mar */
export function fmtDateShort(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return '—';
  return shortDateFormatter.format(d);
}

/** Relative time: "2m ago", "3h ago", "5d ago" etc. */
export function fmtTimeAgo(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  const now = Date.now();
  const diffMs = now - d.getTime();
  if (diffMs < 0) return 'just now';

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;

  return `${Math.floor(months / 12)}y ago`;
}

// ── Aliases (backward compatibility) ────────────────────────────
export const formatCurrency = fmtCurrency;
